// User-id-bucketed AB provider. Post-auth surfaces only (`useUser` hasn't
// resolved on public pages). Each experiment key must belong to exactly one
// provider — don't share keys with the legacy ab-config.tsx.
import { usePostHog } from 'posthog-js/react';
import { useContext, useEffect, useRef, useState } from 'react';

import { useUser } from '@/auth/better/session';
import { computeVariant, type Config, fetchConfig } from '@/lib/ab-testing';
import { ABContextAuthed, getVariantOverrides } from '@/lib/ab-testing/react';

const AB_API_BASE = 'https://app.userjourneys.ai';
// Historical key name — renaming it would drop every user's cached config.
const AB_CONFIG_CACHE_KEY = 'ab_config_cache_clerk';

function getCachedConfig(): Config | null {
  try {
    const raw = localStorage.getItem(AB_CONFIG_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setCachedConfig(config: Config) {
  try {
    localStorage.setItem(AB_CONFIG_CACHE_KEY, JSON.stringify(config));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

export function ABConfigAuthedProvider({ children }: { children: React.ReactNode }) {
  const posthog = usePostHog();
  const { user } = useUser();
  // Bootstrap from cache so the first render has a config immediately
  const [config, setConfig] = useState<Config | null>(() => getCachedConfig());
  const [configLoaded, setConfigLoaded] = useState(() => getCachedConfig() !== null);

  // Cross-device stable bucketing. Anonymous → null → control, no exposure.
  const bucketingId = user?.id ?? null;

  const exposed = useRef(new Set<string>());
  // Override storage shared with the legacy provider (dev-only QA tool).
  const [overrides, setOverrides] = useState(() => getVariantOverrides());

  useEffect(() => {
    const handler = () => setOverrides(getVariantOverrides());
    window.addEventListener('ab-overrides-changed', handler);
    return () => window.removeEventListener('ab-overrides-changed', handler);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      const projectKey = import.meta.env.VITE_UJ_PROJECT_KEY as string | undefined;
      if (!projectKey) {
        if (isMounted) setConfigLoaded(true);
        return;
      }

      const nextConfig = await fetchConfig({
        projectKey,
        apiBase: AB_API_BASE,
      });

      if (isMounted) {
        if (nextConfig) {
          setConfig(nextConfig);
          setCachedConfig(nextConfig);
        }
        setConfigLoaded(true);
      }
    };

    void loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <ABContextAuthed.Provider
      value={{
        config,
        configLoaded,
        posthog,
        userId: bucketingId,
        exposed: exposed.current,
        overrides,
      }}>
      {children}
    </ABContextAuthed.Provider>
  );
}

export function useABConfigLoadedAuthed(): boolean {
  return useContext(ABContextAuthed).configLoaded;
}

export function useVariantAuthed(
  experimentKey: string,
  userAttributes?: Record<string, unknown>,
): string | null {
  const { config, posthog, userId, exposed, overrides } = useContext(ABContextAuthed);

  const override = overrides.get(experimentKey);

  let variant: string | null = null;
  if (!override && config && userId) {
    variant = computeVariant(userId, experimentKey, config, userAttributes);
  }

  useEffect(() => {
    if (override) return;
    if (!variant || !posthog || !config || !userId) return;
    const dedupKey = `${experimentKey}:${variant}:${config.version}`;
    if (exposed.has(dedupKey)) return;
    exposed.add(dedupKey);
    const exp = config.experiments.find((e) => e.key === experimentKey);
    posthog.capture('$experiment_exposure', {
      $experiment_key: experimentKey,
      $variant: variant,
      $layer: exp?.layer ?? '',
    });
  }, [variant, override, experimentKey, posthog, config, userId, exposed]);

  return override ?? variant;
}
